import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { WalletState, Asset, NetworkId } from "@/types";
import { useAuthStore } from "./auth";
import { parseWalletValue } from "@/utils/cardanoValue";
import {
  formatCardanoAddress,
  shortenCardanoAddress,
} from "@/utils/cardanoAddress";

const WALLET_NAME_KEY = "mm_wallet_name";

export const useWalletStore = defineStore("wallet", () => {
  // State
  const isConnected = ref(false);
  const address = ref<string | null>(null);
  const networkId = ref<NetworkId>(0);
  const balance = ref<bigint>(0n);
  const walletName = ref<string | null>(null);
  const assets = ref<Asset[]>([]);
  const isConnecting = ref(false);
  const error = ref<string | null>(null);
  // CIP-30 wallet API reference (for signData etc.)
  let walletApi: any = null;

  // Getters
  const networkName = computed(() => {
    return networkId.value === 0 ? "Preview" : "Mainnet";
  });

  const balanceADA = computed(() => {
    return Number(balance.value) / 1_000_000;
  });

  const displayAddress = computed(() => {
    if (!address.value) return null;
    return formatCardanoAddress(address.value);
  });

  const shortAddress = computed(() => {
    if (!address.value) return null;
    return shortenCardanoAddress(address.value);
  });

  const state = computed<WalletState>(() => ({
    isConnected: isConnected.value,
    address: address.value,
    networkId: networkId.value,
    balance: balance.value,
    walletName: walletName.value,
  }));

  async function syncWalletValue(api: any) {
    const balanceHex = await api.getBalance();
    const parsedValue = parseWalletValue(balanceHex);

    balance.value = parsedValue.lovelace;
    assets.value = parsedValue.assets;
  }

  async function restoreWalletConnection(wallet: string) {
    const cardano = (window as any).cardano;
    if (!cardano || !cardano[wallet]) {
      throw new Error(`Wallet ${wallet} not found`);
    }

    const api = await cardano[wallet].enable();
    walletApi = api;

    const netId = await api.getNetworkId();
    networkId.value = netId as NetworkId;

    const addresses = await api.getUsedAddresses();
    if (addresses.length === 0) {
      const unusedAddresses = await api.getUnusedAddresses();
      address.value = unusedAddresses[0] || null;
    } else {
      address.value = addresses[0];
    }

    await syncWalletValue(api);

    isConnected.value = true;
    walletName.value = wallet;
    localStorage.setItem(WALLET_NAME_KEY, wallet);

    return api;
  }

  // Actions
  async function connect(wallet: string) {
    isConnecting.value = true;
    error.value = null;

    try {
      const api = await restoreWalletConnection(wallet);

      // Initiate auth flow if not already authenticated
      const authStore = useAuthStore();
      if (!authStore.isAuthenticated && address.value) {
        const pkh = getPaymentKeyHash();
        if (pkh) {
          try {
            await authStore.loginWithWallet(
              api,
              address.value,
              pkh,
              wallet,
              networkId.value,
            );
          } catch (authError) {
            console.error(
              "Auth flow error (wallet still connected):",
              authError,
            );
          }
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Failed to connect wallet";
      console.error("Wallet connection error:", e);
    } finally {
      isConnecting.value = false;
    }
  }

  function disconnect() {
    isConnected.value = false;
    address.value = null;
    balance.value = 0n;
    walletName.value = null;
    assets.value = [];
    error.value = null;
    walletApi = null;
    localStorage.removeItem(WALLET_NAME_KEY);

    // Also logout from the auth system
    const authStore = useAuthStore();
    if (authStore.isAuthenticated) {
      authStore.logout();
    }
  }

  /**
   * Reconnect a previously-connected CIP-30 wallet on page reload.
   * This re-enables the wallet API and restores address/balance/assets
   * WITHOUT triggering the auth login flow (the user is already authenticated via JWT).
   */
  async function reconnect(options?: { attemptAuth?: boolean }) {
    const savedWallet = localStorage.getItem(WALLET_NAME_KEY);
    if (!savedWallet) return false;

    const authStore = useAuthStore();
    const shouldAttemptAuth = options?.attemptAuth ?? false;

    isConnecting.value = true;
    error.value = null;

    try {
      const api = await restoreWalletConnection(savedWallet);

      if (shouldAttemptAuth && !authStore.isAuthenticated && address.value) {
        const pkh = getPaymentKeyHash();
        if (pkh) {
          await authStore.loginWithWallet(
            api,
            address.value,
            pkh,
            savedWallet,
            networkId.value,
          );
        }
      }

      return true;
    } catch (e) {
      const cardano = (window as any).cardano;
      if (!cardano || !cardano[savedWallet]) {
        // Wallet extension not available — clear saved name
        localStorage.removeItem(WALLET_NAME_KEY);
      }
      // Reconnect failed silently — user can manually reconnect
      console.warn("Wallet auto-reconnect failed:", e);
      return false;
    } finally {
      isConnecting.value = false;
    }
  }

  async function reauthenticate() {
    const savedWallet =
      walletName.value || localStorage.getItem(WALLET_NAME_KEY);
    if (!savedWallet) return false;

    const authStore = useAuthStore();

    try {
      if (
        !walletApi ||
        !isConnected.value ||
        walletName.value !== savedWallet ||
        !address.value
      ) {
        const reconnected = await reconnect();
        if (!reconnected) return false;
      }

      if (!walletApi || !address.value) return false;

      const pkh = getPaymentKeyHash();
      if (!pkh) {
        error.value = "Could not extract payment key hash from wallet address.";
        return false;
      }

      await authStore.loginWithWallet(
        walletApi,
        address.value,
        pkh,
        walletName.value || savedWallet,
        networkId.value,
      );

      return authStore.isAuthenticated;
    } catch (e) {
      console.warn("Wallet reauthentication failed:", e);
      return false;
    }
  }

  async function refreshBalance() {
    if (!isConnected.value || !walletName.value) return;

    try {
      const cardano = (window as any).cardano;
      const api = await cardano[walletName.value].enable();
      await syncWalletValue(api);
    } catch (e) {
      console.error("Failed to refresh balance:", e);
    }
  }

  /**
   * Get the raw CIP-30 wallet API (for signData, etc.)
   */
  function getWalletApi(): any {
    return walletApi;
  }

  /**
   * Extract the payment key hash from the connected address.
   * Cardano bech32 addresses encode the PKH in the payload.
   * This is a simplified extraction - the actual PKH is the first 28 bytes of the address payload.
   */
  function getPaymentKeyHash(): string | null {
    if (!address.value) return null;
    // For hex-encoded addresses (as returned by CIP-30 getUsedAddresses),
    // the PKH is bytes 1-29 (skipping the header byte)
    try {
      const hex = address.value;
      // CIP-30 returns hex-encoded addresses
      // Header byte (1 byte = 2 hex chars) + PKH (28 bytes = 56 hex chars)
      if (hex.length >= 58) {
        return hex.slice(2, 58);
      }
      return null;
    } catch {
      return null;
    }
  }

  return {
    // State
    isConnected,
    address,
    networkId,
    balance,
    walletName,
    assets,
    isConnecting,
    error,
    // Getters
    networkName,
    balanceADA,
    displayAddress,
    shortAddress,
    state,
    // Actions
    connect,
    disconnect,
    reconnect,
    reauthenticate,
    refreshBalance,
    getWalletApi,
    getPaymentKeyHash,
  };
});
